/**
 * Pure gutter comment layout engine (spec §8.1). No React, no DOM — the
 * `useGutterLayout` hook (../use-gutter-layout.ts) measures anchor Y
 * positions via refs and feeds them in here.
 *
 * Estimate-based by design (spec §18): heights are computed from content
 * shape, never from a rendered card's actual measured size. That's the
 * mock-phase tradeoff the plan calls out explicitly — do not "improve" this
 * with ResizeObserver-measured card heights in this phase.
 */

export interface GutterItem {
  id: string
  /** Top y of this item's target section (comments) or of the composer's
   * anchor section. Comments that target the same section share the same
   * anchorY — that equality is what defines a "section" for alternation. */
  anchorY: number
  kind: 'comment' | 'composer'
  bodyLen: number
  pending: boolean
  openDiff: 'inline' | 'side' | null
  rewriteOpen: boolean
}

export interface PlacedItem {
  id: string
  side: 'right' | 'left'
  top: number
  estHeight: number
}

/** Wide mode threshold (spec §8.1): below this, everything falls right. */
const WIDE_THRESHOLD = 1400

/** Vertical gap enforced between stacked cards on the same side. */
const STACK_GAP = 14

/** Flat composer height (spec §8.1) — the composer never grows an estimate. */
const COMPOSER_HEIGHT = 230

export const GUTTER_CARD_WIDTH = 308

export function gutterLeft(side: 'right' | 'left'): string {
  return side === 'right' ? 'calc(50% + 402px)' : 'max(8px, calc(50% - 710px))'
}

/**
 * 84 + ceil(bodyLen/36)*17, +42 pending, +170 inline diff / +130
 * side-by-side, +100 rewrite open. The composer is a flat 230 regardless of
 * its other fields (spec §8.1).
 */
export function estimateHeight(i: GutterItem): number {
  if (i.kind === 'composer') return COMPOSER_HEIGHT

  let height = 84 + Math.ceil(i.bodyLen / 36) * 17
  if (i.pending) height += 42
  if (i.openDiff === 'inline') height += 170
  else if (i.openDiff === 'side') height += 130
  if (i.rewriteOpen) height += 100
  return height
}

/**
 * Places gutter items per spec §8.1:
 * 1. Sort by anchor y (stable — ties keep input order).
 * 2. Side assignment: wide mode (`containerWidth >= 1400`) alternates
 *    right/left within a section (comments sharing an anchorY), 1st -> R,
 *    2nd -> L, 3rd -> R…; the counter resets at each new section. Narrow
 *    mode puts every comment on the right. The composer always sits on the
 *    right and never consumes an alternation slot.
 * 3. Stacking: per-side cursor enforces no overlap —
 *    `top = max(anchor, cursor)`, `cursor = top + estHeight + gap`.
 */
export function layoutGutter(
  items: GutterItem[],
  opts: { containerWidth: number },
): PlacedItem[] {
  const wide = opts.containerWidth >= WIDE_THRESHOLD

  const order = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.anchorY - b.item.anchorY || a.index - b.index)

  const sideById = new Map<string, 'right' | 'left'>()
  let sectionAnchor: number | null = null
  let sectionSlot = 0
  for (const { item } of order) {
    if (item.kind === 'composer') {
      sideById.set(item.id, 'right')
      continue
    }
    if (!wide) {
      sideById.set(item.id, 'right')
      continue
    }
    if (sectionAnchor !== item.anchorY) {
      sectionAnchor = item.anchorY
      sectionSlot = 0
    }
    sideById.set(item.id, sectionSlot % 2 === 0 ? 'right' : 'left')
    sectionSlot += 1
  }

  const cursor: Record<'right' | 'left', number> = { right: 0, left: 0 }
  const placedById = new Map<string, PlacedItem>()
  for (const { item } of order) {
    const side = sideById.get(item.id) ?? 'right'
    const estHeight = estimateHeight(item)
    const top = Math.max(item.anchorY, cursor[side])
    cursor[side] = top + estHeight + STACK_GAP
    placedById.set(item.id, { id: item.id, side, top, estHeight })
  }

  // Return in the caller's original order for a predictable, stable result.
  return items.map((item) => placedById.get(item.id) as PlacedItem)
}
